import { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import useAuthenticatedUser from '@/hooks/authentication/useAuthenticatedUser';
import AuthenticatedUserContext, { IAuthenticatedUserContext } from '@/contexts/authentication/AuthenticatedUserContext';
import { AuthenticateResponse } from '@/api/dtos';
import IsLoading from '@/components/layouts/IsLoading';
import { useTheme } from '@mui/material';
import { useLocation } from 'react-router';
import BasicAlert from '@/components/layouts/BasicAlert';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import * as Sentry from "@sentry/react";



export interface IAuthenticatedUserProviderProps {
    initial?: AuthenticateResponse | null;
}


export const AuthenticatedUserProvider: FC<PropsWithChildren<IAuthenticatedUserProviderProps>> = ({ initial, children }) => {
    const authenticatedUser = useAuthenticatedUser({ revalidateOnFocus: true, errorRetryCount: 0,  refreshInterval: 600000, fallbackData: initial});

    const theme = useTheme();

    useEffect(() => {
        if (!authenticatedUser.data) {
            Sentry.setUser(null);
            return;
        }

        Sentry.setUser({
            id: authenticatedUser.data.userName,
            username: authenticatedUser.data.userName,
            fullName: authenticatedUser.data.displayName
        })

        return () => {
            Sentry.setUser(null);
        }
    }, [authenticatedUser.data])

    if (!authenticatedUser.loading && !authenticatedUser.validating && !authenticatedUser.data) {
        window.location.href = `/auth/openiddict?redirect=${encodeURIComponent(location.pathname + location.search)}&theme=${theme.palette.mode}`

        return null;
    }

    if (!!authenticatedUser.data && !authenticatedUser.data.roles?.includes('Staff')) {
        
        return (
            <BasicAlert
                severity="error"
                title={'403 Forbidden'}
                description={"User does not have the required permissions to view this resource."}
                icon={faExclamationTriangle}
            />
        )
    }

    return (
        <AuthenticatedUserContext.Provider value={authenticatedUser}>
            <IsLoading name="authenticated-user" loading={authenticatedUser.loading} validating={authenticatedUser.validating}/>
            {children}
        </AuthenticatedUserContext.Provider>
    );
}

export default AuthenticatedUserProvider;
